import React from 'react';
import {
  DEVICE_BLACKLIST_CATEGORIES,
  DEVICE_WHITELIST_CATEGORIES,
  UrlAccessCategory
} from '../data/deviceWhitelistUrls';

type ListType = 'whitelist' | 'blacklist';

const listMeta: Record<ListType, { title: string; description: string; categories: UrlAccessCategory[] }> = {
  whitelist: {
    title: '화이트리스트',
    description: '항목별로 URL을 확인하고, 필요한 것만 선택해 복사할 수 있습니다.',
    categories: DEVICE_WHITELIST_CATEGORIES
  },
  blacklist: {
    title: '블랙리스트',
    description: '차단할 URL 목록입니다. deviceWhitelistUrls.ts의 DEVICE_BLACKLIST_CATEGORIES에 항목을 추가하세요.',
    categories: DEVICE_BLACKLIST_CATEGORIES
  }
};

const normalizeText = (value: string) => value.trim().toLowerCase();

const copyToClipboard = async (text: string) => {
  await navigator.clipboard.writeText(text);
};

const UrlAccessListSection: React.FC = () => {
  const [activeList, setActiveList] = React.useState<ListType>('whitelist');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [selectedUrls, setSelectedUrls] = React.useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = React.useState<Set<string>>(new Set());
  const [copiedLabel, setCopiedLabel] = React.useState<string | null>(null);

  const { title, description, categories } = listMeta[activeList];

  const filteredCategories = React.useMemo(() => {
    const query = normalizeText(searchQuery);

    return categories
      .map((category) => {
        const categoryMatches =
          query.length === 0 ||
          normalizeText([category.name, category.description ?? '', ...category.urls].join(' ')).includes(query);

        const matchedUrls =
          query.length === 0
            ? category.urls
            : category.urls.filter((url) => normalizeText(url).includes(query));

        if (!categoryMatches && matchedUrls.length === 0) {
          return null;
        }

        return {
          ...category,
          urls: categoryMatches ? category.urls : matchedUrls
        };
      })
      .filter((category): category is UrlAccessCategory => category !== null);
  }, [categories, searchQuery]);

  const visibleUrls = React.useMemo(
    () => filteredCategories.flatMap((category) => category.urls),
    [filteredCategories]
  );

  React.useEffect(() => {
    setSelectedUrls(new Set());
    setExpandedCategories(new Set());
  }, [activeList]);

  React.useEffect(() => {
    setSelectedUrls((prev) => {
      const visibleSet = new Set(visibleUrls);
      const next = new Set<string>();
      prev.forEach((url) => {
        if (visibleSet.has(url)) {
          next.add(url);
        }
      });
      return next;
    });
  }, [visibleUrls]);

  const showCopiedFeedback = (label: string) => {
    setCopiedLabel(label);
    window.setTimeout(() => setCopiedLabel((prev) => (prev === label ? null : prev)), 1500);
  };

  const handleCopy = async (urls: string[], label: string) => {
    if (urls.length === 0) {
      return;
    }

    try {
      await copyToClipboard(urls.join('\n'));
      showCopiedFeedback(label);
    } catch (error) {
      console.error('목록 복사 실패:', error);
    }
  };

  const toggleUrl = (url: string) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      if (next.has(url)) {
        next.delete(url);
      } else {
        next.add(url);
      }
      return next;
    });
  };

  const toggleCategoryUrls = (urls: string[], selectAll: boolean) => {
    setSelectedUrls((prev) => {
      const next = new Set(prev);
      urls.forEach((url) => {
        if (selectAll) {
          next.add(url);
        } else {
          next.delete(url);
        }
      });
      return next;
    });
  };

  const toggleCategoryExpanded = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  const selectedUrlList = React.useMemo(
    () => visibleUrls.filter((url) => selectedUrls.has(url)),
    [visibleUrls, selectedUrls]
  );

  const allVisibleSelected = visibleUrls.length > 0 && visibleUrls.every((url) => selectedUrls.has(url));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
      <div
        style={{
          border: '1px solid var(--Color-line-normal, #F0F0F0)',
          borderRadius: '12px',
          backgroundColor: 'var(--Color-background-main, #F4F6FA)',
          padding: '18px'
        }}
      >
        <div style={{ fontSize: '18px', lineHeight: '27px', fontWeight: 700, marginBottom: '8px' }}>안내</div>
        <p style={{ margin: 0, fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
          항목을 펼쳐 URL을 확인하고, 체크박스로 필요한 항목만 골라 복사할 수 있습니다. Google Admin 콘솔 등에는 URL을 한 줄에
          하나씩 붙여넣으세요.
        </p>
      </div>

      <div
        style={{
          border: '1px solid var(--Color-line-normal, #F0F0F0)',
          borderRadius: '12px',
          backgroundColor: 'white',
          padding: '18px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {(['whitelist', 'blacklist'] as ListType[]).map((listType) => (
            <button
              key={listType}
              type="button"
              onClick={() => setActiveList(listType)}
              aria-pressed={activeList === listType}
              style={{
                border:
                  activeList === listType
                    ? '1px solid var(--Color-fill-brand-color, #FF6F0F)'
                    : '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '8px',
                backgroundColor:
                  activeList === listType ? 'var(--Color-background-main, #F4F6FA)' : 'white',
                padding: '8px 14px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                color:
                  activeList === listType
                    ? 'var(--Color-fill-brand-color, #FF6F0F)'
                    : 'var(--Color-text-normal, #161616)'
              }}
            >
              {listMeta[listType].title} ({listMeta[listType].categories.reduce((sum, c) => sum + c.urls.length, 0)})
            </button>
          ))}
        </div>

        <div>
          <div style={{ fontSize: '18px', lineHeight: '27px', fontWeight: 700 }}>{title}</div>
          <p style={{ margin: '4px 0 0', fontSize: '13px', lineHeight: '20px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
            {description}
          </p>
        </div>

        <input
          type="search"
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          placeholder="항목명·URL 검색"
          aria-label="항목명·URL 검색"
          style={{
            width: '100%',
            border: '1px solid var(--Color-line-normal, #F0F0F0)',
            borderRadius: '8px',
            padding: '10px 12px',
            fontSize: '14px',
            lineHeight: '20px',
            boxSizing: 'border-box'
          }}
        />

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => toggleCategoryUrls(visibleUrls, !allVisibleSelected)}
            disabled={visibleUrls.length === 0}
            style={actionButtonStyle(visibleUrls.length === 0)}
          >
            {allVisibleSelected ? '전체 선택 해제' : `전체 선택 (${visibleUrls.length})`}
          </button>
          <button
            type="button"
            onClick={() => void handleCopy(selectedUrlList, 'selected')}
            disabled={selectedUrlList.length === 0}
            style={primaryButtonStyle(selectedUrlList.length === 0)}
          >
            {copiedLabel === 'selected' ? '복사됨' : `선택 복사 (${selectedUrlList.length})`}
          </button>
          <button
            type="button"
            onClick={() => void handleCopy(visibleUrls, 'all')}
            disabled={visibleUrls.length === 0}
            style={primaryButtonStyle(visibleUrls.length === 0)}
          >
            {copiedLabel === 'all' ? '복사됨' : `표시 항목 전체 복사 (${visibleUrls.length})`}
          </button>
          <button
            type="button"
            onClick={() =>
              setExpandedCategories(
                new Set(filteredCategories.length === expandedCategories.size ? [] : filteredCategories.map((c) => c.id))
              )
            }
            disabled={filteredCategories.length === 0}
            style={actionButtonStyle(filteredCategories.length === 0)}
          >
            {filteredCategories.length > 0 && expandedCategories.size === filteredCategories.length
              ? '모두 접기'
              : '모두 펼치기'}
          </button>
        </div>

        {filteredCategories.length === 0 ? (
          <p style={{ margin: 0, fontSize: '14px', lineHeight: '21px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
            {categories.length === 0 ? '등록된 항목이 없습니다.' : '검색 결과가 없습니다.'}
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {filteredCategories.map((category) => {
              const isExpanded = expandedCategories.has(category.id) || searchQuery.length > 0;
              const categorySelectedCount = category.urls.filter((url) => selectedUrls.has(url)).length;
              const allCategorySelected =
                category.urls.length > 0 && category.urls.every((url) => selectedUrls.has(url));

              return (
                <div
                  key={category.id}
                  style={{
                    border: '1px solid var(--Color-line-normal, #F0F0F0)',
                    borderRadius: '10px',
                    backgroundColor: 'var(--Color-background-main, #F4F6FA)',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '12px 14px',
                      backgroundColor: 'white'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={allCategorySelected}
                      ref={(element) => {
                        if (element) {
                          element.indeterminate =
                            categorySelectedCount > 0 && categorySelectedCount < category.urls.length;
                        }
                      }}
                      onChange={() => toggleCategoryUrls(category.urls, !allCategorySelected)}
                      aria-label={`${category.name} 전체 선택`}
                      style={{ marginTop: '3px', flexShrink: 0 }}
                    />
                    <button
                      type="button"
                      onClick={() => toggleCategoryExpanded(category.id)}
                      aria-expanded={isExpanded}
                      style={{
                        flex: 1,
                        minWidth: 0,
                        border: 'none',
                        background: 'none',
                        padding: 0,
                        textAlign: 'left',
                        cursor: 'pointer'
                      }}
                    >
                      <div style={{ fontSize: '15px', lineHeight: '22px', fontWeight: 700, color: 'var(--Color-text-normal, #161616)' }}>
                        {category.name}{' '}
                        <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
                          ({category.urls.length}개{categorySelectedCount > 0 ? ` · ${categorySelectedCount}개 선택` : ''})
                        </span>
                      </div>
                      {category.description ? (
                        <p style={{ margin: '2px 0 0', fontSize: '12px', lineHeight: '18px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
                          {category.description}
                        </p>
                      ) : null}
                    </button>
                    <div style={{ display: 'flex', gap: '6px', flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                      <button
                        type="button"
                        onClick={() => void handleCopy(category.urls, `category-${category.id}`)}
                        style={actionButtonStyle(false, '12px')}
                      >
                        {copiedLabel === `category-${category.id}` ? '복사됨' : '항목 복사'}
                      </button>
                      <button
                        type="button"
                        onClick={() => toggleCategoryExpanded(category.id)}
                        aria-label={isExpanded ? `${category.name} 접기` : `${category.name} 펼치기`}
                        style={actionButtonStyle(false, '12px')}
                      >
                        {isExpanded ? '접기' : '펼치기'}
                      </button>
                    </div>
                  </div>

                  {isExpanded ? (
                    <div style={{ padding: '0 14px 12px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {category.urls.map((url) => (
                        <label
                          key={`${category.id}-${url}`}
                          style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '8px',
                            padding: '8px 10px',
                            borderRadius: '8px',
                            backgroundColor: selectedUrls.has(url) ? 'white' : 'transparent',
                            border: selectedUrls.has(url) ? '1px solid var(--Color-fill-brand-color, #FF6F0F)' : '1px solid transparent',
                            cursor: 'pointer'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedUrls.has(url)}
                            onChange={() => toggleUrl(url)}
                            style={{ marginTop: '2px', flexShrink: 0 }}
                          />
                          <span
                            style={{
                              fontSize: '13px',
                              lineHeight: '20px',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                              wordBreak: 'break-all',
                              color: 'var(--Color-text-normal, #161616)'
                            }}
                          >
                            {url}
                          </span>
                        </label>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}

        {selectedUrlList.length > 0 ? (
          <div
            style={{
              border: '1px solid var(--Color-line-normal, #F0F0F0)',
              borderRadius: '8px',
              padding: '12px',
              backgroundColor: 'var(--Color-background-main, #F4F6FA)'
            }}
          >
            <div style={{ fontSize: '13px', lineHeight: '20px', fontWeight: 700, marginBottom: '8px' }}>
              선택 미리보기 ({selectedUrlList.length}개)
            </div>
            <textarea
              readOnly
              value={selectedUrlList.join('\n')}
              aria-label="선택한 URL 미리보기"
              onFocus={(event) => event.currentTarget.select()}
              style={{
                width: '100%',
                minHeight: '120px',
                resize: 'vertical',
                border: '1px solid var(--Color-line-normal, #F0F0F0)',
                borderRadius: '8px',
                padding: '10px',
                fontSize: '12px',
                lineHeight: '18px',
                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                backgroundColor: 'white',
                boxSizing: 'border-box'
              }}
            />
          </div>
        ) : null}

        <p style={{ margin: 0, fontSize: '12px', lineHeight: '18px', color: 'var(--Color-text-assistive-strong, #4C4C4C)' }}>
          목록 수정: <code style={{ fontSize: '12px' }}>src/info-chief/data/deviceWhitelistUrls.ts</code>
        </p>
      </div>
    </div>
  );
};

const actionButtonStyle = (disabled: boolean, fontSize = '13px'): React.CSSProperties => ({
  border: '1px solid var(--Color-line-normal, #F0F0F0)',
  borderRadius: '8px',
  backgroundColor: 'white',
  padding: '8px 10px',
  fontSize,
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1
});

const primaryButtonStyle = (disabled: boolean): React.CSSProperties => ({
  border: '1px solid var(--Color-line-normal, #F0F0F0)',
  borderRadius: '8px',
  backgroundColor: 'var(--Color-fill-brand-color, #FF6F0F)',
  color: 'white',
  padding: '8px 12px',
  fontSize: '13px',
  fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.5 : 1
});

export default UrlAccessListSection;
